import { useState, MouseEvent, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Box,
  Tooltip,
  useTheme,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppSocket } from '../providers/AppSocketContext';
import { useTheme as useCustomTheme } from '../hooks/useTheme';
import {
  Logout,
  ManageAccountsOutlined,
  SettingsOutlined,
  AdminPanelSettingsOutlined,
  Fullscreen,
  FullscreenExit,
  PowerSettingsNew,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { Util } from '../util/Util';
import { canRead } from '../util/access';
import { ADMIN_PANEL_URL, API_BASE_URL } from '../util/constants';
import { useUI } from '../providers/MoleUIContext';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PosLayoutToggle from '../features/sale/PosLayoutToggle';
import {
  PosLayoutMode,
  readStoredPosLayoutMode,
  setPosLayoutMode,
} from '../features/sale/posLayouts';

/** Fired when POS user requests disconnect from the top bar. */
export const POS_DISCONNECT_EVENT = 'pos:request-disconnect';

interface Props {
  onLogout: () => void;
  onViewProfile: () => void;
  sideMenuMinimized?: boolean;
  setSideMenuMinimized?: (value: boolean) => void;
  pageTitle?: string;
  /** Active POS terminal session: show fullscreen + disconnect controls. */
  posMode?: boolean;
}

const TopBar = ({
  onLogout,
  onViewProfile,
  sideMenuMinimized,
  setSideMenuMinimized,
  pageTitle,
  posMode = false,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [posLayoutMode, setPosLayoutModeState] = useState<PosLayoutMode>(() =>
    readStoredPosLayoutMode(),
  );
  const { session, api, access } = useAuth();
  const { appSettings } = useAppSocket();
  const { setLoadingMessage } = useUI();
  const { setTheme, isDark } = useCustomTheme();
  const { t, i18n } = useTranslation();
  const muiTheme = useTheme();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || muiTheme.palette.primary.main;
  const navigate = useNavigate();
  const nextModeLabel = isDark ? t('common:ui.modes.light') : t('common:ui.modes.dark');
  const currentLang = (i18n.language || 'en').toUpperCase().slice(0, 2);
  const iconButtonSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(muiTheme.palette.divider, 0.35)}`,
    bgcolor: alpha(muiTheme.palette.background.paper, 0.7),
    width: { xs: 34, sm: 36, md: 40 },
    height: { xs: 34, sm: 36, md: 40 },
    transition: 'background-color var(--motion-base) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard), transform var(--motion-fast) var(--motion-ease-standard), box-shadow var(--motion-base) var(--motion-ease-standard)',
    '&:focus-visible': {
      outline: `2px solid ${alpha(muiTheme.palette.primary.main, 0.7)}`,
      outlineOffset: 1,
    },
    '&:hover': {
      bgcolor: alpha(muiTheme.palette.primary.main, 0.08),
      borderColor: alpha(muiTheme.palette.primary.main, 0.45),
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0px) scale(0.98)',
    },
  };

  // Add new state for language menu
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);

  // You probably want to persist this in context / localStorage / i18n provider
  // For now we'll use simple local state + example setter

  const handleLangMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const changeLanguage = async (lang: 'en' | 'fr') => {

    const payload = {
      lang: lang
    }


    const res = await api.put(`/update/account/${session?.user_id}`, payload);

    if (res) {
      if (res.status === 201 || res.status === 204) {
        i18n.changeLanguage(lang);
        toast.success(t("common:toast.success.languageChange"), { autoClose: 3000 });
      } else if (res.status === 409) {
        toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
      } else {
        console.error("Failed to send data:", res);
      }
      // TODO: handle 400 bad requests
    }


    handleLangMenuClose();
  };

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleConfig = () => {
    handleMenuClose();
    navigate('/admin_settings');
  };

  const openAdminLogin = () => {
    const loginUrl = API_BASE_URL ? `${API_BASE_URL}admin_login` : ADMIN_PANEL_URL;
    if (loginUrl) {
      window.open(loginUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAdminPanel = async () => {
    handleMenuClose();
    if (!canRead(access, 'admin_portal')) {
      openAdminLogin();
      return;
    }
    if (!API_BASE_URL && !ADMIN_PANEL_URL) return;

    setLoadingMessage("PROCESS");
    try {
      const res = await api.post('/admin_portal', {}, { skipErrorToast: true });
      if (res?.status === 200 && res.data?.enter_url) {
        window.open(res.data.enter_url, '_blank', 'noopener,noreferrer');
        return;
      }
      const loginUrl = res?.data?.login_url || (API_BASE_URL ? `${API_BASE_URL}admin_login` : ADMIN_PANEL_URL);
      if (loginUrl) {
        window.open(loginUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      openAdminLogin();
    } finally {
      setLoadingMessage(null);
    }
  };

  const handleLogout = () => {
    handleMenuClose();
    // Parent (Main) calls auth.logout — server revoke + multi-tab clear.
    onLogout();
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    onFsChange();
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (!posMode) return;
    setPosLayoutModeState(readStoredPosLayoutMode());
  }, [posMode]);

  const handleFullscreenToggle = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  const handlePosDisconnect = useCallback(() => {
    window.dispatchEvent(new CustomEvent(POS_DISCONNECT_EVENT));
  }, []);

  const handlePosLayoutChange = useCallback((mode: PosLayoutMode) => {
    setPosLayoutModeState(mode);
    setPosLayoutMode(mode);
  }, []);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        height: 'var(--topbar-height)',
        bgcolor: alpha(muiTheme.palette.background.paper, 0.92),
        color: 'var(--text-primary)',
        borderBottom: `1px solid ${alpha(themeColor, 0.28)}`,
        borderTop: `3px solid ${themeColor}`,
        boxShadow: `0 4px 24px ${alpha(muiTheme.palette.common.black, isDark ? 0.32 : 0.08)}`,
        backdropFilter: 'blur(10px)',
        backgroundImage: `linear-gradient(180deg, ${alpha(themeColor, 0.06)} 0%, ${alpha(muiTheme.palette.background.paper, 0.92)} 100%)`,
        transition: 'background-color var(--motion-base) var(--motion-ease-standard), color var(--motion-base) var(--motion-ease-standard), box-shadow var(--motion-base) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard)',
      }}
    >
      <Toolbar
        sx={{
          minHeight: 'var(--topbar-height) !important',
          height: 'var(--topbar-height)',
          position: 'relative',
          justifyContent: 'space-between',
          px: { xs: 1.5, md: 2.5 },
        }}
      >
        {/* Left: Logo + Business Name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            minWidth: 0,
            flex: 1,
            // Keep left content from overlapping the absolute center cluster
            maxWidth: { xs: '38%', sm: '34%', md: '32%' },
            pr: 1,
            zIndex: 2,
          }}
        >
          {setSideMenuMinimized &&
            <IconButton
              color="inherit"
              aria-label={t('common:ui.tooltips.openMenu')}
              onClick={() => {
                setSideMenuMinimized!(!sideMenuMinimized);
              }}
              edge="start"
              sx={iconButtonSx}
            >
              <MenuIcon />
            </IconButton>
          }
          <Box
            component="img"
            src={appSettings?.BIZ_LOGO.value || '/logo.png'}
            alt={t('common:settings.logoAlt')}
            sx={{
              height: { xs: 32, sm: 36 },
              width: { xs: 32, sm: 36 },
              objectFit: 'contain',
              borderRadius: 1,
              border: `1px solid ${alpha(muiTheme.palette.divider, 0.5)}`,
              p: 0.25,
              bgcolor: alpha(muiTheme.palette.background.paper, 0.75),
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                maxWidth: { xs: '30vw', sm: '28vw', md: '36vw' },
              }}
            >
              {appSettings?.BIZ_NAME_FULL.value || t('common:settings.appFallbackName')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: { xs: pageTitle ? 'block' : 'none', sm: 'block' },
                maxWidth: { xs: '38vw', sm: '28vw', md: '36vw' },
              }}
            >
              {pageTitle && (
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, fontWeight: 600, color: themeColor }}>
                  {pageTitle}
                </Box>
              )}
              <Box component="span" sx={{ display: { xs: pageTitle ? 'none' : 'inline', sm: 'inline' } }}>
                {appSettings?.BIZ_DEPARTMENT.value || t('common:settings.defaultDepartment')} -{' '}
                {appSettings?.BIZ_BRANCH.value || t('common:settings.defaultBranch')}
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* True horizontal center (independent of left/right widths) */}
        {(pageTitle || posMode) && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: posMode
                ? 'flex'
                : { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.75, sm: 1.25 },
              maxWidth: { xs: '44%', sm: '48%', md: '52%' },
              zIndex: 1,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            {pageTitle && (
              <Chip
                label={pageTitle}
                size="small"
                sx={{
                  maxWidth: posMode ? { xs: 72, sm: 100, md: 140 } : 'min(280px, 100%)',
                  fontWeight: 600,
                  borderColor: alpha(themeColor, 0.35),
                  bgcolor: alpha(themeColor, 0.08),
                  color: themeColor,
                  flexShrink: 0,
                  display: posMode
                    ? { xs: 'none', sm: 'inline-flex' }
                    : undefined,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
                variant="outlined"
              />
            )}
            {posMode && (
              <PosLayoutToggle
                compact
                mode={posLayoutMode}
                onChange={handlePosLayoutChange}
              />
            )}
          </Box>
        )}

        {/* Right: POS controls (when active) + Theme + Language + User Menu */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flex: 1,
            minWidth: 0,
            // Mirror left so absolute center stays clear of controls
            maxWidth: { xs: '38%', sm: '34%', md: '32%' },
            ml: 'auto',
            pl: 1,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.4, sm: 0.5, md: 0.75 },
              flexShrink: 0,
              p: 0.5,
              borderRadius: 2.5,
              border: `1px solid ${alpha(muiTheme.palette.divider, 0.55)}`,
              bgcolor: alpha(muiTheme.palette.background.paper, 0.55),
            }}
          >
          {posMode && (
            <>
              <Tooltip
                title={
                  isFullscreen
                    ? t('common:pos.exitFullscreen')
                    : t('common:pos.fullscreen')
                }
              >
                <IconButton
                  onClick={handleFullscreenToggle}
                  size="small"
                  aria-label={
                    isFullscreen
                      ? t('common:pos.exitFullscreen')
                      : t('common:pos.fullscreen')
                  }
                  sx={iconButtonSx}
                >
                  {isFullscreen ? (
                    <FullscreenExit sx={{ fontSize: { xs: 20, sm: 22 } }} />
                  ) : (
                    <Fullscreen sx={{ fontSize: { xs: 20, sm: 22 } }} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('common:pos.disconnect')}>
                <IconButton
                  onClick={handlePosDisconnect}
                  size="small"
                  aria-label={t('common:pos.disconnect')}
                  color="error"
                  sx={{
                    ...iconButtonSx,
                    borderColor: alpha(muiTheme.palette.error.main, 0.45),
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: alpha(muiTheme.palette.error.main, 0.1),
                      borderColor: alpha(muiTheme.palette.error.main, 0.65),
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <PowerSettingsNew sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </IconButton>
              </Tooltip>

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 0.25,
                  borderColor: alpha(muiTheme.palette.divider, 0.65),
                  display: { xs: 'none', sm: 'block' },
                }}
              />
            </>
          )}

          {/* Theme Toggle */}
          <Tooltip title={t("common:ui.tooltips.switchTheme", { mode: nextModeLabel })}>
            <IconButton
              onClick={toggleTheme}
              size="small"
              aria-label={t('common:ui.tooltips.switchTheme', { mode: nextModeLabel })}
              sx={iconButtonSx}
            >
              <FontAwesomeIcon
                icon={isDark ? faSun : faMoon}
                style={{
                  fontSize: 18,
                  color: isDark ? 'var(--secondary-color)' : 'var(--text-secondary)',
                  transition: 'color var(--motion-base) var(--motion-ease-standard)',
                }}
              />
            </IconButton>
          </Tooltip>

          {/* ─── Language Switcher ─── */}
          <Tooltip title={t("common:ui.tooltips.language")}>
            <IconButton
              size="small"
              onClick={handleLangMenuOpen}
              aria-label={t('common:ui.tooltips.language')}
              color={i18n.language === 'fr' ? 'primary' : 'inherit'}
              sx={{
                ...iconButtonSx,
                width: { xs: 34, sm: 36, md: 64 },
                justifyContent: { xs: 'center', md: 'flex-start' },
                px: { xs: 0, sm: 0, md: 1 },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: 18, sm: 20 },
                },
              }}
            >
              <LanguageIcon />
              <Box
                component="span"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  ml: 0.7,
                  fontSize: '0.58rem',
                  fontWeight: 800,
                  letterSpacing: 0.35,
                  px: 0.55,
                  py: 0.1,
                  borderRadius: 0.6,
                  bgcolor: alpha(muiTheme.palette.primary.main, 0.14),
                  color: 'primary.main',
                  lineHeight: 1.2,
                }}
              >
                {currentLang}
              </Box>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleLangMenuClose}
            MenuListProps={{
              'aria-label': t('common:ui.tooltips.language'),
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transitionDuration={{ appear: 120, enter: 200, exit: 160 }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: { xs: 140, sm: 160 },
                borderRadius: 2,
                border: `1px solid ${alpha(muiTheme.palette.divider, 0.6)}`,
                boxShadow: `0 12px 24px ${alpha(muiTheme.palette.common.black, 0.15)}`,
                backdropFilter: 'blur(6px)',
                transition: 'box-shadow var(--motion-base) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard), background-color var(--motion-base) var(--motion-ease-standard)',
              },
            }}
          >
            <MenuItem
              selected={i18n.language === 'en'}
              sx={{
                py: 1,
                px: 1.5,
                transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                '&:hover': { pl: 2 },
              }}
              onClick={() => changeLanguage('en')}
            >
              {t('common:languages.english')}
            </MenuItem>
            <MenuItem
              selected={i18n.language === 'fr'}
              sx={{
                py: 1,
                px: 1.5,
                transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                '&:hover': { pl: 2 },
              }}
              onClick={() => changeLanguage('fr')}
            >
              {t('common:languages.french')}
            </MenuItem>
          </Menu>

          <Typography
            variant="caption"
            sx={{
              display: { xs: 'none', md: 'block' },
              fontWeight: 600,
              color: 'text.secondary',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 0.5,
            }}
          >
            {session?.username}
          </Typography>

          {/* User Avatar Menu */}
          <Tooltip title={session?.username}>
            <IconButton
              color="inherit"
              aria-label={t('common:ui.tooltips.userMenu')}
              onClick={handleMenuOpen}
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                bgcolor: Util.stringToColor(session?.username || ''),
                color: 'white',
                border: `2px solid ${alpha(muiTheme.palette.background.paper, 0.8)}`,
                '&:hover': {
                  bgcolor: Util.stringToColor((session?.username || '').split('').reverse().join('')),
                },
                transition: 'background-color var(--motion-base) var(--motion-ease-standard), transform var(--motion-fast) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard)',
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 30, sm: 32 },
                  height: { xs: 30, sm: 32 },
                  bgcolor: 'inherit',
                  color: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {session?.username?.charAt(0) || '?'}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-label': t('common:ui.tooltips.userMenu'),
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transitionDuration={{ appear: 120, enter: 200, exit: 160 }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                borderRadius: 2.5,
                border: `1px solid ${alpha(muiTheme.palette.divider, 0.65)}`,
                boxShadow: `0 16px 32px ${alpha(muiTheme.palette.common.black, 0.16)}`,
                overflow: 'hidden',
                backdropFilter: 'blur(8px)',
                transition: 'box-shadow var(--motion-base) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard), background-color var(--motion-base) var(--motion-ease-standard)',
              },
            }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {session?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t('common:ui.labels.roles')} : {session?.roles?.map((role: any) => role).join(', ') || t('common:ui.labels.na')}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={handleProfile}
              sx={{
                py: 1,
                px: 1.5,
                transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                '&:hover': { pl: 2 },
              }}
            >
              <ListItemIcon >
                <ManageAccountsOutlined />
              </ListItemIcon>
              {t("common:ui.buttons.profile")}
            </MenuItem>
            <Divider />
            {canRead(access, 'admin_portal') || canRead(access, 'settings')
              ? [
                  ...(canRead(access, 'admin_portal') && ADMIN_PANEL_URL
                    ? [
                        <MenuItem
                          key="menu-admin-panel"
                          onClick={handleAdminPanel}
                          sx={{
                            py: 1,
                            px: 1.5,
                            transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                            '&:hover': { pl: 2 },
                          }}
                        >
                          <ListItemIcon>
                            <AdminPanelSettingsOutlined />
                          </ListItemIcon>
                          {t("common:ui.buttons.adminPanel")}
                        </MenuItem>,
                      ]
                    : []),
                  ...(canRead(access, 'settings')
                    ? [
                        <MenuItem
                          key="menu-config"
                          onClick={handleConfig}
                          sx={{
                            py: 1,
                            px: 1.5,
                            transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                            '&:hover': { pl: 2 },
                          }}
                        >
                          <ListItemIcon>
                            <SettingsOutlined />
                          </ListItemIcon>
                          {t("common:ui.buttons.configurations")}
                        </MenuItem>,
                      ]
                    : []),
                  <Divider key="menu-config-divider" />,
                ]
              : null}
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1,
                px: 1.5,
                transition: 'padding var(--motion-fast) var(--motion-ease-standard), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-fast) var(--motion-ease-standard)',
                '&:hover': { pl: 2 },
              }}
            >
              <ListItemIcon sx={{
                color: 'error.main',
              }}>
                <Logout />
              </ListItemIcon>
              {t("common:ui.buttons.logout")}
            </MenuItem>
          </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;